/**
 * Traduções nossas — telas gerais: Configurações, tour, celular, conexão com
 * banco, avisos de erro e barras de status.
 *
 * A chave é a frase em inglês, byte a byte como está em `locale/en.json`.
 * Ver o cabeçalho de `pt-BR.ts` para o porquê deste diretório existir.
 */
export const geral: Record<string, string> = {
  // --- Configurações: moedas ---
  'Israeli New Shekel': 'Novo shekel israelense',
  'Iranian Rial': 'Rial iraniano',
  'Macedonian Denar': 'Dinar macedônio',
  'Mexican Peso': 'Peso mexicano',
  'Peruvian Sol': 'Sol peruano',
  'Pakistani Rupee': 'Rupia paquistanesa',
  'Uruguayan Peso': 'Peso uruguaio',

  // --- Configurações: recursos experimentais ---
  'Deprecated: this feature will be removed in a future release. Use Excel formula mode (Rule formulae) instead.':
    'Descontinuado: este recurso vai sair numa versão futura. Use o modo de fórmula do Excel (fórmulas de regra) no lugar.',
  'Mobile calculator': 'Calculadora no celular',
  'Monte Carlo Analysis Report': 'Relatório de análise Monte Carlo',
  'Enable Banking sync (EU banks)': 'Enable Banking (bancos da Europa)',
  'Akahu Bank Sync (NZ banks)': 'Akahu (bancos da Nova Zelândia)',
  'Custom CSS override active — click to edit':
    'CSS personalizado ativo — clique pra editar',
  'Custom CSS is active': 'O CSS personalizado está ativo',

  // --- Configurações: exportar / importar ---
  'This export is larger than Actual can safely re-import. You may not be able to restore this backup.':
    'Esta exportação é maior do que o app consegue reimportar com segurança. Pode ser que você não consiga restaurar esta cópia.',
  'This export is larger than the memory available on this device. Restoring it here may fail.':
    'Esta exportação é maior que a memória disponível neste dispositivo. Restaurar aqui pode falhar.',

  // --- Tour ---
  Finish: 'Concluir',
  '{{current}} of {{total}}': '{{current}} de {{total}}',
  'Your Budget': 'Seu orçamento',
  'Saved This Month': 'Guardado neste mês',
  'This summary compares your income and expenses to show how much you saved this month. Rather than rolling funds over, a tracking budget plans each month on its own.':
    'Este resumo compara o que entrou com o que saiu pra mostrar quanto sobrou no mês. Em vez de acumular saldo, cada mês é planejado por conta própria.',
  Categories: 'Categorias',
  'Month by Month': 'Mês a mês',
  'Every month gets its own budget. Use the month picker to move between months, and the calendar icons on the left to choose how many months are shown side by side.':
    'Cada mês tem o seu próprio planejamento. Use o seletor de mês pra navegar e os ícones de calendário à esquerda pra escolher quantos meses aparecem lado a lado.',
  'Getting Around': 'Como navegar',
  'Add Your Accounts': 'Adicione suas contas',
  'Getting Help': 'Onde pedir ajuda',

  // --- Celular ---
  'Add your first account': 'Adicione sua primeira conta',
  'Accounts hold your transactions, like everyday spending, savings, credit cards, or cash. Add one to start tracking your money.':
    'As contas guardam seus lançamentos — gastos do dia a dia, poupança, cartão de crédito ou dinheiro. Adicione uma pra começar a acompanhar.',
  'Linked accounts will appear here. Use a provider above to link your bank, or create an account from the Accounts tab and connect it here later.':
    'As contas conectadas aparecem aqui. Use um provedor acima pra conectar seu banco, ou crie a conta na aba Contas e conecte depois.',
  Reset: 'Restaurar padrão',
  'Who paid you?': 'Quem te pagou?',
  'Who did you pay?': 'Pra quem você pagou?',
  'Failed to create schedule': 'Não foi possível criar o agendamento',
  'Schedule created, but the original transaction could not be deleted':
    'O agendamento foi criado, mas o lançamento original não pôde ser excluído',
  'Select an account': 'Escolha uma conta',
  'Add tag': 'Adicionar etiqueta',
  'Request Location': 'Pedir localização',
  'Unlock reconciled transaction': 'Destravar lançamento conciliado',
  'Unclear transaction': 'Desmarcar como confirmado',
  'Clear transaction': 'Marcar como confirmado',
  from: 'de',

  // --- Primeira tela / servidor ---
  'Connect to a server': 'Conectar a um servidor',
  Connect: 'Conectar',
  'Use the current domain': 'Usar o domínio atual',

  // --- Nomes dos temas (hard-coded em style/theme.tsx, ver nomeDeTema.ts) ---
  // 'Light' vira 'Claro' (o upstream diz 'Modo Claro', que destoa de Escuro/Meia-noite)
  Light: 'Claro',
  Dark: 'Escuro',
  Midnight: 'Meia-noite',

  // --- Primeira conta / importação ---
  "<0>Let's add your first account.</0> Accounts hold your transactions, like everyday spending, savings, credit cards, or cash. You can connect to your bank to import transactions automatically, or add them yourself.":
    '<0>Vamos criar sua primeira conta.</0> As contas guardam seus lançamentos — gastos do dia a dia, poupança, cartão de crédito ou dinheiro. Você pode conectar seu banco pra baixar os lançamentos automaticamente, ou lançar você mesmo.',
  'Once the account is created, you can also <2>import QIF/OFX/QFX files</2> into it.':
    'Depois de criar a conta, você também pode <2>importar arquivos QIF/OFX/QFX</2> pra ela.',
  'No accounts yet. Once a provider is set up, use <2>Link bank account</2> to connect your bank and create your accounts automatically. You can also add accounts manually and connect them here later.':
    'Nenhuma conta ainda. Depois de configurar um provedor, use <2>Conectar conta do banco</2> pra ligar seu banco e criar as contas automaticamente. Você também pode criar as contas na mão e conectá-las aqui depois.',

  // --- Conexão com banco ---
  'Reset {{provider}} credentials': 'Redefinir as credenciais do {{provider}}',
  Providers: 'Provedores',
  'Set up a bank sync provider, then link new accounts or connect an existing Actual account.':
    'Configure um provedor de conexão bancária e depois conecte suas contas do banco — novas ou já existentes aqui.',
  global: 'em todos os arquivos',
  'this budget only': 'só neste arquivo',
  'Not configured': 'Não configurado',
  'Set up': 'Configurar',
  'Link bank account': 'Conectar conta do banco',
  'Reset credentials before setting credentials for this budget file.':
    'Redefina as credenciais antes de configurar as deste arquivo.',
  'Failed to reset {{provider}}': 'Não foi possível redefinir o {{provider}}',
  'Budget file ID is required.': 'O ID do arquivo é obrigatório.',
  'Error when trying to contact SimpleFIN':
    'Erro ao tentar falar com o SimpleFIN',
  'Error when trying to contact Enable Banking':
    'Erro ao tentar falar com o Enable Banking',
  'Error when trying to contact Akahu': 'Erro ao tentar falar com o Akahu',
  'Link a European bank account to automatically download transactions.':
    'Conecte uma conta de banco europeu pra baixar os lançamentos automaticamente.',
  'Link a North American bank account to automatically download transactions.':
    'Conecte uma conta de banco norte-americano pra baixar os lançamentos automaticamente.',
  'Link a Brazilian bank account to automatically download transactions.':
    'Conecte uma conta de banco brasileiro pra baixar os lançamentos automaticamente.',
  'Link a New Zealand bank account to automatically download transactions.':
    'Conecte uma conta de banco neozelandês pra baixar os lançamentos automaticamente.',
  'Link a European bank account via Enable Banking, a free alternative to GoCardless for PSD2-supported banks.':
    'Conecte uma conta de banco europeu pelo Enable Banking, alternativa gratuita ao GoCardless para bancos com PSD2.',
  'Missing auth URL or state': 'Faltou a URL de autorização ou o estado',
  'There was an error linking the account to Akahu. Please try again.':
    'Houve um erro ao conectar a conta ao Akahu. Tente de novo.',
  'There was an error linking the account to Enable Banking. Please try again.':
    'Houve um erro ao conectar a conta ao Enable Banking. Tente de novo.',
  'Missing authorization parameters.': 'Faltam parâmetros de autorização.',
  'Failed to complete authorization.':
    'Não foi possível concluir a autorização.',
  'You can close this window and try again.':
    'Você pode fechar esta janela e tentar de novo.',

  // --- Avisos e erros ---
  "Actual couldn't load a critical backend worker. Reload the page to try again; if the problem persists, do a hard refresh to clear any stale cached assets.":
    'O app não conseguiu carregar um componente essencial. Recarregue a página; se continuar, force a atualização (Cmd+Shift+R) pra limpar o cache.',
  'Your environment does not support SharedArrayBuffer. You may experience data loss or degraded functionality. Click to learn more.':
    'Este navegador não suporta SharedArrayBuffer. Você pode perder dados ou ter funções limitadas. Clique pra saber mais.',
  Warning: 'Atenção',
  'Local file, not connected to a server':
    'Arquivo local, sem conexão com servidor',
  'This file is larger than the maximum supported size of {{maxSizeMB}} MB, sorry! Visit https://actualbudget.org/contact/ for support.':
    'Este arquivo é maior que o limite de {{maxSizeMB}} MB. Veja https://actualbudget.org/contact/ pra suporte.',
  'The file "{{entryName}}" in this archive is larger than the maximum supported size of {{maxSizeMB}} MB, sorry! Visit https://actualbudget.org/contact/ for support.':
    'O arquivo "{{entryName}}" dentro deste pacote é maior que o limite de {{maxSizeMB}} MB. Veja https://actualbudget.org/contact/ pra suporte.',
  'The uncompressed contents of this archive are larger than the maximum supported size of {{maxSizeMB}} MB, sorry! Visit https://actualbudget.org/contact/ for support.':
    'O conteúdo descompactado deste pacote é maior que o limite de {{maxSizeMB}} MB. Veja https://actualbudget.org/contact/ pra suporte.',
  'This archive contains an entry with an unsafe file name: "{{entryName}}".':
    'Este pacote tem um arquivo com nome inseguro: "{{entryName}}".',
  'This archive contains more than one entry named "{{entryName}}".':
    'Este pacote tem mais de um arquivo chamado "{{entryName}}".',
  'This file could not be imported, sorry! Visit https://actualbudget.org/contact/ for support.':
    'Não foi possível importar este arquivo. Veja https://actualbudget.org/contact/ pra suporte.',
  'This budget could not be loaded because it uses a newer database schema than this version of Actual supports. Make sure you are using the latest version, then try again.':
    'Não foi possível abrir este arquivo: ele usa um formato de banco mais novo do que esta versão do app entende. Atualize o app e tente de novo.',
  'Unable to decrypt file {{fileName}}. To change your key, first download this file with the proper password.':
    'Não foi possível descriptografar o arquivo {{fileName}}. Pra trocar a chave, baixe primeiro este arquivo com a senha correta.',

  // --- Lançamentos, etiquetas e agendamentos (avulsos) ---
  'Manage table columns': 'Gerenciar colunas da tabela',
  'Tag List': 'Lista de etiquetas',
  'Templating is deprecated and will be removed in a future release. Switch this action to a formula instead.':
    'Os modelos foram descontinuados e vão sair numa versão futura. Troque esta ação por uma fórmula.',
  'Upcoming length': 'Antecedência',
  'Use global default': 'Usar o padrão geral',
  'How far in advance this schedule appears as upcoming':
    'Com quanta antecedência este agendamento aparece como próximo',
  'Sync both transfer dates': 'Sincronizar as duas datas da transferência',
  Unhide: 'Mostrar de novo',
  'Show hidden tags': 'Mostrar etiquetas ocultas',
  'Cleared: verified against your account':
    'Confirmado: conferido com o extrato da conta',
  'Reconciled: locked after reconciliation':
    'Conciliado: travado depois da conciliação',
  'Upcoming scheduled transaction': 'Agendamento a vencer',
  'Due scheduled transaction': 'Agendamento vencendo hoje',
  'Missed scheduled transaction': 'Agendamento atrasado',
  'Transaction status': 'Situação do lançamento',
  'Running balance': 'Saldo acumulado',
  'View Schedule': 'Ver agendamento',
  'Select month': 'Escolher o mês',
  'There was an error renaming the tag. Please try again.':
    'Houve um erro ao renomear a etiqueta. Tente de novo.',
  'There was an error hiding the tags. Please try again.':
    'Houve um erro ao ocultar as etiquetas. Tente de novo.',
  'has all tags': 'tem todas as etiquetas',
  'has any tag': 'tem qualquer etiqueta',

  // --- O nome do app e o vocabulário do menu ---
  // O dono abriu a tela de Configurações e leu "Actual é uma aplicação…".
  // É a mesma queixa da porta de entrada: o nome deles aparecendo onde ele
  // esperava o dele. O upstream traduz as mesmas chaves e a nossa vence em
  // runtime — as duas convivem no bundle, e isso é o esperado.
  '<0>Actual</0> is a super fast privacy-focused app for managing your finances.':
    '<0>FinApp</0> é um app rápido e privado pra cuidar do seu dinheiro.',
  // "Ajustes" é o nome do item no menu; o título da página tem que ser o
  // mesmo, senão o dono clica em "Ajustes" e chega numa tela chamada
  // "Configurações".
  Settings: 'Ajustes',
};
